import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Mail, MessageSquare, Send, Users, Plus, Search,
  CheckCircle2, XCircle, Clock, Eye, Trash2, RefreshCw,
  Filter, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;

const BlastCommunications = () => {
  const { token } = useAuth();
  
  // State
  const [blasts, setBlasts] = useState([]);
  const [gmailStatus, setGmailStatus] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [showNewBlast, setShowNewBlast] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [selectedBlast, setSelectedBlast] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state
  const [blastForm, setBlastForm] = useState({
    name: '',
    blast_type: 'email',
    subject: '',
    email_content: '',
    sms_content: '',
    recipient_type: 'clients',
    recipient_ids: [],
    selectAll: false
  });

  // Fetch data
  const fetchBlasts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/blasts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBlasts(data);
      }
    } catch (err) {
      console.error('Error fetching blasts:', err);
    }
  }, [token]);

  const fetchEmailStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/blasts/email/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGmailStatus(data);
      }
    } catch (err) {
      console.error('Error fetching email status:', err);
    }
  }, [token]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.filter(c => c.is_active));
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, [token]);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVendors(data.filter(v => v.is_active));
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBlasts(),
        fetchEmailStatus(),
        fetchClients(),
        fetchVendors()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchBlasts, fetchEmailStatus, fetchClients, fetchVendors]);

  // Create Blast
  const handleCreateBlast = async () => {
    if (!blastForm.name) {
      toast.error('Please enter a campaign name');
      return;
    }

    if (blastForm.blast_type !== 'sms' && !blastForm.subject) {
      toast.error('Please enter an email subject');
      return;
    }

    if (blastForm.blast_type !== 'sms' && !blastForm.email_content) {
      toast.error('Please enter email content');
      return;
    }

    if (blastForm.blast_type !== 'email' && !blastForm.sms_content) {
      toast.error('Please enter SMS content');
      return;
    }

    if (blastForm.recipient_ids.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    try {
      const res = await fetch(`${API}/api/blasts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: blastForm.name,
          blast_type: blastForm.blast_type,
          subject: blastForm.subject,
          email_content: blastForm.email_content,
          sms_content: blastForm.sms_content,
          recipient_type: blastForm.recipient_type,
          recipient_ids: blastForm.recipient_ids
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Campaign created with ${data.total_recipients} recipients`);
        setShowNewBlast(false);
        resetBlastForm();
        fetchBlasts();
      } else {
        toast.error(data.detail || 'Failed to create campaign');
      }
    } catch (err) {
      toast.error('Error creating campaign');
    }
  };

  const resetBlastForm = () => {
    setBlastForm({
      name: '',
      blast_type: 'email',
      subject: '',
      email_content: '',
      sms_content: '',
      recipient_type: 'clients',
      recipient_ids: [],
      selectAll: false
    });
  };

  // Send Blast
  const handleSendBlast = async (blastId) => {
    try {
      const res = await fetch(`${API}/api/blasts/${blastId}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Campaign is now sending!');
        fetchBlasts();
      } else {
        toast.error(data.detail || 'Failed to send campaign');
      }
    } catch (err) {
      toast.error('Error sending campaign');
    }
  };

  // Delete Blast
  const handleDeleteBlast = async (blastId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const res = await fetch(`${API}/api/blasts/${blastId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Campaign deleted');
        fetchBlasts();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete campaign');
      }
    } catch (err) {
      toast.error('Error deleting campaign');
    }
  };

  // View Recipients
  const handleViewRecipients = async (blast) => {
    setSelectedBlast(blast);
    try {
      const res = await fetch(`${API}/api/blasts/${blast.blast_id}/recipients?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data);
        setShowRecipients(true);
      }
    } catch (err) {
      toast.error('Error fetching recipients');
    }
  };

  // Handle recipient selection
  const handleRecipientToggle = (id) => {
    setBlastForm(prev => {
      const newIds = prev.recipient_ids.includes(id)
        ? prev.recipient_ids.filter(i => i !== id)
        : [...prev.recipient_ids, id];
      return { ...prev, recipient_ids: newIds };
    });
  };

  const handleSelectAll = () => {
    const targetList = blastForm.recipient_type === 'clients' ? clients : vendors;
    const idField = blastForm.recipient_type === 'clients' ? 'client_id' : 'vendor_id';
    
    if (blastForm.selectAll) {
      setBlastForm(prev => ({ ...prev, recipient_ids: [], selectAll: false }));
    } else {
      const allIds = targetList.map(item => item[idField]);
      setBlastForm(prev => ({ ...prev, recipient_ids: allIds, selectAll: true }));
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      sending: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  // Filter blasts
  const filteredBlasts = blasts.filter(b => {
    const matchesSearch = b.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blast-communications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email & SMS Blasts</h1>
          <p className="text-gray-500 mt-1">Send mass communications to clients and vendors</p>
        </div>
        <Button onClick={() => setShowNewBlast(true)} data-testid="new-blast-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Email Status Banner */}
      {gmailStatus.configured && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <div className="flex-1">
            <p className="font-medium text-green-800">Email Sending Ready</p>
            <p className="text-sm text-green-600">
              Emails will be sent from: {gmailStatus.email}
            </p>
          </div>
        </div>
      )}

      {!gmailStatus.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">Email Not Configured</p>
            <p className="text-sm text-amber-600">
              Email sending is not available. Contact administrator to configure.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-campaigns"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sending">Sending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchBlasts}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredBlasts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">No campaigns yet</p>
                    <p className="text-sm">Create your first email or SMS blast campaign</p>
                  </td>
                </tr>
              ) : (
                filteredBlasts.map((blast) => (
                  <tr key={blast.blast_id} className="hover:bg-gray-50" data-testid={`blast-row-${blast.blast_id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{blast.name}</p>
                        {blast.subject && (
                          <p className="text-sm text-gray-500 truncate max-w-xs">{blast.subject}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {blast.blast_type === 'email' && <Mail className="h-4 w-4 text-blue-600" />}
                        {blast.blast_type === 'sms' && <MessageSquare className="h-4 w-4 text-green-600" />}
                        {blast.blast_type === 'both' && (
                          <>
                            <Mail className="h-4 w-4 text-blue-600" />
                            <MessageSquare className="h-4 w-4 text-green-600" />
                          </>
                        )}
                        <span className="capitalize text-sm">{blast.blast_type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewRecipients(blast)}
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <Users className="h-4 w-4" />
                        {blast.total_recipients}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(blast.status)}
                    </td>
                    <td className="px-6 py-4">
                      {blast.stats && (
                        <div className="text-xs space-y-1">
                          {blast.blast_type !== 'sms' && (
                            <div className="flex gap-2">
                              <span className="text-green-600">{blast.stats.email_sent} sent</span>
                              {blast.stats.email_failed > 0 && (
                                <span className="text-red-600">{blast.stats.email_failed} failed</span>
                              )}
                            </div>
                          )}
                          {blast.blast_type !== 'email' && (
                            <div className="flex gap-2">
                              <span className="text-green-600">{blast.stats.sms_sent} SMS</span>
                              {blast.stats.sms_failed > 0 && (
                                <span className="text-red-600">{blast.stats.sms_failed} failed</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(blast.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {blast.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleSendBlast(blast.blast_id)}
                              data-testid={`send-blast-${blast.blast_id}`}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Send
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteBlast(blast.blast_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {blast.status === 'sending' && (
                          <Badge className="bg-yellow-100 text-yellow-700 animate-pulse">
                            <Clock className="h-3 w-3 mr-1" />
                            In Progress
                          </Badge>
                        )}
                        {blast.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewRecipients(blast)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Results
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Blast Dialog */}
      <Dialog open={showNewBlast} onOpenChange={setShowNewBlast}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>
              Send an email or SMS blast to your clients or vendors
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Campaign Name */}
            <div>
              <label className="text-sm font-medium text-gray-700">Campaign Name</label>
              <Input
                placeholder="e.g., Holiday Greetings 2025"
                value={blastForm.name}
                onChange={(e) => setBlastForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
                data-testid="campaign-name-input"
              />
            </div>

            {/* Blast Type */}
            <div>
              <label className="text-sm font-medium text-gray-700">Message Type</label>
              <Select
                value={blastForm.blast_type}
                onValueChange={(v) => setBlastForm(prev => ({ ...prev, blast_type: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="blast-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" /> Email Only
                    </div>
                  </SelectItem>
                  <SelectItem value="sms">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> SMS Only
                    </div>
                  </SelectItem>
                  <SelectItem value="both">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <MessageSquare className="h-4 w-4" /> Both Email & SMS
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Email Content */}
            {blastForm.blast_type !== 'sms' && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email Content
                </h4>
                <div>
                  <label className="text-sm text-gray-600">Subject Line</label>
                  <Input
                    placeholder="Email subject"
                    value={blastForm.subject}
                    onChange={(e) => setBlastForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="mt-1"
                    data-testid="email-subject-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Email Body (HTML supported)</label>
                  <Textarea
                    placeholder="Write your email content here. You can use HTML tags for formatting."
                    value={blastForm.email_content}
                    onChange={(e) => setBlastForm(prev => ({ ...prev, email_content: e.target.value }))}
                    className="mt-1 min-h-[150px] font-mono text-sm"
                    data-testid="email-content-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Use &lt;p&gt;, &lt;strong&gt;, &lt;a href="..."&gt; for formatting
                  </p>
                </div>
              </div>
            )}

            {/* SMS Content */}
            {blastForm.blast_type !== 'email' && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h4 className="font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> SMS Content
                </h4>
                <div>
                  <Textarea
                    placeholder="Write your SMS message (max 160 characters for single SMS)"
                    value={blastForm.sms_content}
                    onChange={(e) => setBlastForm(prev => ({ ...prev, sms_content: e.target.value }))}
                    className="mt-1"
                    maxLength={320}
                    data-testid="sms-content-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {blastForm.sms_content.length}/320 characters
                    {blastForm.sms_content.length > 160 && ' (will be sent as multiple SMS)'}
                  </p>
                </div>
              </div>
            )}

            {/* Recipients Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Select Recipients</h4>
                <Select
                  value={blastForm.recipient_type}
                  onValueChange={(v) => setBlastForm(prev => ({
                    ...prev,
                    recipient_type: v,
                    recipient_ids: [],
                    selectAll: false
                  }))}
                >
                  <SelectTrigger className="w-40" data-testid="recipient-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clients">Clients</SelectItem>
                    <SelectItem value="vendors">Vendors</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox
                  id="selectAll"
                  checked={blastForm.selectAll}
                  onCheckedChange={handleSelectAll}
                />
                <label htmlFor="selectAll" className="text-sm font-medium cursor-pointer">
                  Select All {blastForm.recipient_type === 'clients' ? 'Clients' : 'Vendors'}
                  ({blastForm.recipient_type === 'clients' ? clients.length : vendors.length})
                </label>
              </div>

              <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                {(blastForm.recipient_type === 'clients' ? clients : vendors).map((item) => {
                  const id = blastForm.recipient_type === 'clients' ? item.client_id : item.vendor_id;
                  const email = blastForm.recipient_type === 'clients' ? item.billing_email : item.email;
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleRecipientToggle(id)}
                    >
                      <Checkbox
                        checked={blastForm.recipient_ids.includes(id)}
                        onCheckedChange={() => handleRecipientToggle(id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {email && <span className="mr-3">{email}</span>}
                          {item.phone && <span>{item.phone}</span>}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {email && <Mail className="h-3 w-3 text-gray-400" />}
                        {item.phone && <MessageSquare className="h-3 w-3 text-gray-400" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-sm text-gray-600">
                <strong>{blastForm.recipient_ids.length}</strong> recipients selected
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewBlast(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBlast} data-testid="create-campaign-btn">
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipients Dialog */}
      <Dialog open={showRecipients} onOpenChange={setShowRecipients}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Campaign Recipients - {selectedBlast?.name}
            </DialogTitle>
            <DialogDescription>
              {recipients.length} recipients • Status: {selectedBlast?.status}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto max-h-96">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Contact</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">SMS Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {recipients.map((r) => (
                  <tr key={r.recipient_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{r.type}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {r.email && <div>{r.email}</div>}
                      {r.phone && <div>{r.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {r.email_status && (
                        <div className="flex items-center gap-1">
                          {r.email_status === 'sent' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                          {r.email_status === 'pending' && <Clock className="h-4 w-4 text-yellow-500" />}
                          {r.email_status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                          <span className="text-xs capitalize">{r.email_status}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.sms_status && (
                        <div className="flex items-center gap-1">
                          {r.sms_status === 'sent' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                          {r.sms_status === 'pending' && <Clock className="h-4 w-4 text-yellow-500" />}
                          {r.sms_status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                          <span className="text-xs capitalize">{r.sms_status}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecipients(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlastCommunications;
